import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { MoreVertical } from '@lyearn/icons';
import { Avatar, InfoPill, Stack, Typography } from '@lyearn/ui';
import { TypographyColors, TypographyVariants } from '@lyearn/ui';
import AvatarOverlay from '@/components/User/layouts/UserInfoTooltip/components/AvatarOverlay';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import { convertKnowledgeAssetSubTypeVariant } from '@/modules/knowledge-asset/types';
import { avatars } from '@/modules/session/utils/getRandomAvatar';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { KnowledgeAssetContentType, ResourceStatusEnum } from '@/types/schema';

import { logoStyle } from '../../styles';
import { renderKnowledgeAssetStatusPill } from '../StatusPill';

export interface UserDetailsOverrides {
  Email?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Name?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Icon?: {
    className?: string;
    size?: number;
  };
}

function PersonaCardGrid({
  persona,
  index,
  hideActionMenu = true,
  isLearner,
  editOnClick,
  showStatusPill = false,
}: {
  persona: ContentsFragment;
  index: number;
  hideActionMenu?: boolean;
  isLearner?: boolean;
  editOnClick?: boolean;
  showStatusPill?: boolean;
}) {
  const { t } = useTranslation();
  const { id, name, cover } = persona;
  const knowledgeAssetSubTypeVariant = (persona as KnowledgeAssetContentType)
    ?.knowledgeAssetSubTypeVariant;
  const industries = (persona as KnowledgeAssetContentType).industries?.map((industry) =>
    convertKnowledgeAssetSubTypeVariant(industry),
  );
  const industriesString =
    industries && industries.length
      ? industries?.[0] + (industries?.length > 1 ? ` +${industries?.length - 1}` : '')
      : null;

  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();
  const defaultAvatar = avatars[0].url;
  const AvatarComponent = (
    <Avatar className={logoStyle} size={56} src={cover?.url || defaultAvatar} />
  );
  const onClick = useCallback(() => {
    if (editOnClick) {
      navigation.replace(routes.KnowledgeAssetEditLearner(id, 'persona'), { appendQuery: false });
    } else {
      const isDraft = (persona as KnowledgeAssetContentType).status === ResourceStatusEnum.Draft;
      let path = isDraft
        ? routes.KnowledgeAssetEditor(id, 'persona')
        : routes.KnowledgeAssetDetails(id, 'persona');
      if (isLearner || !isAdminRoute) {
        let learnerPath = routes.KnowledgeAssetLearnerDetails(id, 'persona');
        navigation.push(learnerPath, { appendQuery: false });
        return;
      }
      navigation.replace(path, { appendQuery: false });
    }
  }, [id, editOnClick]);

  const statusPill = useMemo(
    () =>
      showStatusPill
        ? renderKnowledgeAssetStatusPill(persona as KnowledgeAssetContentType, t)
        : null,
    [showStatusPill, persona, t],
  );

  return (
    <Stack
      className="w-[23.2rem] rounded-12 border cursor-pointer border-field px-20 py-20 hover:shadow-01 transition-shadow xs:min-h-full xs:w-full"
      direction="col"
      gapY={16}
      onClick={onClick}>
      <Stack gapX={12} justifyContent="between">
        <AvatarOverlay>{AvatarComponent}</AvatarOverlay>

        {hideActionMenu ? null : (
          <Stack className="flex-shrink-0">
            <MoreVertical className="text-icon-secondary" height={20} width={20} />
          </Stack>
        )}
      </Stack>
      <Stack direction="col">
        <Stack className="w-full overflow-hidden">
          <Typography numberOfLines={1} variant="heading-xs">
            {name}
          </Typography>
        </Stack>
        <Stack>
          <Typography color="text-text-secondary" numberOfLines={1} variant="label-02">
            {industriesString}
          </Typography>
        </Stack>
      </Stack>
      <Stack className="mt-auto">
        {statusPill ? (
          statusPill
        ) : knowledgeAssetSubTypeVariant ? (
          <InfoPill
            appearance="primary"
            className="py-6 px-14 h-[3.6rem]"
            label={convertKnowledgeAssetSubTypeVariant(knowledgeAssetSubTypeVariant)}
            size="large"
            type="text"
          />
        ) : null}
      </Stack>
    </Stack>
  );
}

export default PersonaCardGrid;
